import {
  getFocusedRouteNameFromRoute,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { captureException } from "@sentry/react-native";
import { Alert } from "react-native";
import { IconButton, useTheme } from "react-native-paper";

import { StackScreenProps, TabScreen, productFormRoutes } from "./types";
import { Header, TabBar } from "../../../components";
import useSnackbar from "../../../contexts/SnackbarContext";
import useProductFormAPI from "../contexts/ProductFormAPIContext";
import useProductFormFormik from "../hooks/useProductFormFormik";
import { confirmAlert, unsavedChangesAlert } from "../utils";

export default function TabNavigatorHeader() {
  const { colors } = useTheme();
  const { showMessage, updateMessage } = useSnackbar();

  const { dirty, isEditing } = useProductFormFormik();
  const { isCloningProduct, deleteProduct, goBack } = useProductFormAPI();

  function onBackPress() {
    unsavedChangesAlert({ isDirty: dirty, goBack });
  }

  async function confirmDelete() {
    try {
      showMessage("Eliminando producto...");
      await deleteProduct();
      goBack();
      updateMessage("Producto eliminado");
    } catch (e) {
      captureException(e);
    }
  }

  function onDeletePress() {
    confirmAlert({
      title: "Eliminar producto",
      body: "¿Estás seguro que deseas eliminar este producto?",
      cancelButton: { text: "No", onPress: undefined },
      confirmButton: { text: "Sí", onPress: confirmDelete },
    });
  }

  function onClosePress() {
    goBack();
  }

  const word = isCloningProduct ? "Duplicar" : isEditing ? "Editar" : "Crear";
  const title = `${word} producto`;

  return (
    <Header
      title={title}
      onBackPress={onBackPress}
      onClosePress={onClosePress}
      childrenHorizontalAlignMode="edges"
      rightAction={
        isEditing && (
          <IconButton
            icon="delete"
            iconColor={colors.error}
            onPress={onDeletePress}
          />
        )
      }
    >
      <ProductFormTabBar />
    </Header>
  );
}

type NavProps = StackScreenProps<"Tabs">;
function ProductFormTabBar() {
  const { description, prices, inventory, variants } =
    productFormRoutes.tabs;

  const routes = [description, prices, inventory, variants];

  const route = useRoute<NavProps["route"]>();
  const navigation = useNavigation<NavProps["navigation"]>();

  const currentRoute = (getFocusedRouteNameFromRoute(route) ??
    description) as TabScreen;

  function nav(screen: TabScreen) {
    navigation.navigate(productFormRoutes.tabs.navigator, { screen });
  }

  return (
    <TabBar
      values={routes}
      selectedValue={currentRoute}
      selectValue={nav}
      padEdges
    />
  );
}
